import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { FormConfig } from '@symplicity/syng';
import { RegularFairDay } from '@csm/shared';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EventByIdResponse, EventRegistration, EventRegistrationsResponse, EventsService, EventStatus, EventTypes } from '../../services';
import { ActionButton } from './action-buttons/action-buttons.component';

export interface EventViewData {
    event: EventByIdResponse;
    actionButtons: ActionButton[];
    tabData: any;
}

export interface EventOverviewData {
    event: EventByIdResponse;
    isVirtualFair: boolean;
    employers: {
        total: number;
        list: EventRegistration[];
    }
    regularFairDays: RegularFairDay[];
    actionButtons: ActionButton[];
}

export interface EventAttendingEmployersData {
    eventId: string;
    eventStatus: EventStatus;
    isVirtualFair: boolean;
    isLawCsm: boolean;
    filtersFormConfig: FormConfig;
}

export interface EventAttendingEmployerViewData {
    eventId: string;
    eventData: any;
    isVirtualFair: boolean;
}

@Injectable()
export class ResolveEventView  {
    constructor(
        private eventsService: EventsService,
        private translateService: TranslateService
    ) {}

    public resolve(route: ActivatedRouteSnapshot): Observable<EventViewData> {
        const { eventId } = route.params;

        return this.eventsService.getEventById(eventId).pipe(
            map<EventByIdResponse, EventViewData>(event => ({
                event,
                actionButtons: getActionButtons(event, this.translateService),
                tabData: getTabData(event)
            }))
        );
    }
}

@Injectable()
export class ResolveEventOverview  {
    constructor(
        private eventsService: EventsService,
        private translateService: TranslateService
    ) {}

    public resolve(route: ActivatedRouteSnapshot): Observable<EventOverviewData> {
        const eventId = route.parent.data.resolvedData.event.event_id;

        return forkJoin([
            this.eventsService.getEventById(eventId),
            this.eventsService.getEventRegistrations(eventId, {}, true)
        ]).pipe(
            map<[EventByIdResponse, EventRegistrationsResponse], EventOverviewData>(([event, employersAttending]) => {
                const isVirtualFair = event.type._id === EventTypes.VirtualCareerFair;

                return {
                    event,
                    isVirtualFair,
                    employers: {
                        total: employersAttending.total,
                        list: employersAttending.models
                    },
                    regularFairDays: isVirtualFair ? [] : getRegularFairDays(event),
                    actionButtons: getActionButtons(event, this.translateService)
                }
            })
        );
    }
}

@Injectable()
export class ResolveEventAttendingEmployers  {
    constructor(
        private eventsService: EventsService,
        private window: Window
    ) {}

    public resolve(route: ActivatedRouteSnapshot): Observable<EventAttendingEmployersData> {
        const eventId = route.parent.data.resolvedData.event.event_id;

        return forkJoin([
            this.eventsService.getEventById(eventId),
            this.eventsService.getEventFiltersFormConfig(eventId)
        ]).pipe(
            map<[EventByIdResponse, FormConfig], EventAttendingEmployersData>(([event, filtersFormConfig]) => {
                const isVirtualFair = event.type._id === EventTypes.VirtualCareerFair;

                return {
                    eventId,
                    eventStatus: event.status,
                    isVirtualFair,
                    isLawCsm: this.window.ngData.isLawCsm,
                    filtersFormConfig
                };
            })
        );
    }
}

@Injectable()
export class ResolveEventAttendingEmployerView  {
    constructor(
        private eventsService: EventsService,
    ) {}

    public resolve(route: ActivatedRouteSnapshot): Observable<EventAttendingEmployerViewData> {
        const eventId = route.parent.data.resolvedData.event.event_id;

        return this.eventsService.getEventById(eventId).pipe(
            map(eventData => {
                const isVirtualFair = eventData.type._id === EventTypes.VirtualCareerFair;
                return {
                    eventData,
                    eventId,
                    isVirtualFair
                };
            })
        );
    }

}

const getActionButtons = (
    event: EventByIdResponse,
    translateService: TranslateService
): ActionButton[] => {
    const actionButtons: ActionButton[] = [];

    if (event.student_visible === '1') {
        if (event.status === 'upcoming' || event.status === 'ongoing') {
            actionButtons.push({
                id: 'attendAsStudent',
                label: translateService.instant('event view.Attend as a Student'),
                type: 'anchor',
                target: '_blank',
                href: `/students/app/career-fairs/${event.event_id}/overview`
            });
        } else {
            actionButtons.push({
                id: 'loginAsStudent',
                label: translateService.instant('event view.Login as a Student'),
                type: 'anchor',
                target: '_blank',
                href: `/students/app/career-fairs/${event.event_id}/overview`
            });
        }
    }

    if (event.accept_regs === '1') {
        if (event.status === 'upcoming' || event.status === 'ongoing') {
            actionButtons.push({
                id: 'registerAsEmployer',
                label: translateService.instant('event view.Register as an Employer'),
                type: 'button'
            });
        } else {
            actionButtons.push({
                id: 'loginAsEmployer',
                label: translateService.instant('event view.Login as an Employer'),
                type: 'anchor',
                target: '_blank',
                href: `/employers/event/careerfairs/${event.event_id}/overview`
            });
        }
    }

    return actionButtons;
};

const getTabData = (event: EventByIdResponse): any => {
    const tabData = {
        tabId: 'cf-event-view',
        tabList: [
            {
                active: undefined,
                externalUrl: false,
                title: 'event view.Overview',
                url: 'overview'
            }
        ]
    };

    tabData.tabList.push({
        active: undefined,
        externalUrl: false,
        title: 'event view.Attending Employers',
        url: 'employers'
    });

    return tabData;
};

const getRegularFairDays = (event: EventByIdResponse): RegularFairDay[] => {
    return event.day
        .filter(day => !(
            day.hide_from_students._id === '1' &&
            day.hide_on_regform._id === 'yes'
        ))
        .sort((a, b) => a.daystart > b.daystart ? 1 : -1)
        .map(day => new RegularFairDay({
            id: day.eventday_id,
            start: day.daystart,
            end: day.dayend,
            isSingleDay: day.day_type._id === '1',
            date: '',
            label: day.label,
            addressLabel: day.location.address.address_text,
            mapUrl: day.location.locationAddressDisplayString ? `https://maps.google.com/?q=${day.location.locationAddressDisplayString.replace(/(?:\r\n|\r|\n)/g, ', ').replace(',,', ',')}` : null,
            address: day.location.locationAddressDisplayString,
            boothmapCount: 0,
            parkingInformation: day.parking_info || day.location.parking_info || ''
        }));
};